// src/pages/admin/FlexPriceAdmin.jsx
import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import LoadingSpinner from "../../components/LoadingSpinner";
import { toast } from "react-toastify";
import {
  getFlexPrices,
  upsertFlexPrice,
  deleteFlexPrice,
} from "../../api/flexPrices";
import { PlusIcon, TrashIcon } from "@heroicons/react/24/outline";

export default function FlexPriceAdmin() {
  const qc = useQueryClient();

  // 1) fetch existing ratess
  const { data: serverRates = [], isLoading } = useQuery({
    queryKey: ["flexPrices"],
    queryFn: getFlexPrices,
  });

  // 2) local editable copy
  const [rates, setRates] = useState([]);
  useEffect(() => {
    setRates(serverRates.reverse());
  }, [serverRates]);

  // 3) upsert mutation (create or update)
  const { mutate: saveRate, isLoading: isSaving } = useMutation({
    mutationFn: upsertFlexPrice,
    onSuccess: () => {
      qc.invalidateQueries(["flexPrices"]);
      toast.success("✔️ Rate saved");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Save failed");
    },
  });

  // 4) delete mutation
  const { mutate: removeRateById, isLoading: isDeleting } = useMutation({
    mutationFn: deleteFlexPrice,
    onSuccess: () => {
      qc.invalidateQueries(["flexPrices"]);
      toast.success("🗑️ Rate deleted");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "❌ Delete failed");
    },
  });

  // handlers to update local state
  const updateGroup = (idx, group) =>
    setRates((rs) => rs.map((r, i) => (i === idx ? { ...r, group } : r)));

  const updateRate = (idx, rateExVAT) =>
    setRates((rs) => rs.map((r, i) => (i === idx ? { ...r, rateExVAT } : r)));

  // add a new blank row
  //   const addRate = () => setRates((rs) => [...rs, { group: "", rateExVAT: "" }]);
  const addRate = () => setRates((rs) => [{ group: "", rateExVAT: "" }, ...rs]);

  // remove locally and call delete if it existed on server
  const removeRate = (idx) => {
    const toDelete = rates[idx];
    if (toDelete._id) removeRateById(toDelete._id);
    setRates((rs) => rs.filter((_, i) => i !== idx));
  };

  if (isLoading) return <LoadingSpinner />;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center max-w-3xl mx-auto">
        <h2 className="text-2xl font-bold mb-5 text-center">
          Manage Flex Prices
        </h2>
        <button
          onClick={addRate}
          className="inline-flex items-center px-3 py-1 bg-surface rounded hover:bg-accent text-sm"
        >
          <PlusIcon className="h-4 w-4 mr-1" /> Add Rate
        </button>
      </div>

      <div className="bg-surface rounded-lg shadow mb-4 max-w-3xl mx-auto p-2">
        {rates.length === 0 ? (
          <p className="p-4 text-center">No flex prices found.</p>
        ) : (
          <table className="w-full table-auto">
            <thead className="bg-surface">
              <tr>
                <th className="p-1 text-left text-sm">Group Code</th>
                <th className="p-1 text-left text-sm">Rate ex VAT</th>
                <th className="p-1 text-center text-sm">Save</th>
                <th className="p-1 text-center text-sm">Delete</th>
              </tr>
            </thead>
            <tbody>
              {rates.map((r, idx) => (
                <tr
                  key={r._id || idx}
                  className="hover:bg-lightsec dark:hover:bg-darksec"
                >
                  <td className="p-1">
                    <input
                      type="text"
                      value={r.group}
                      onChange={(e) => updateGroup(idx, e.target.value)}
                      placeholder="Grp"
                      className="w-fit border px-1 py-1 rounded text-sm"
                    />
                  </td>
                  <td className="p-1">
                    <input
                      type="number"
                      step="0.01"
                      value={r.rateExVAT}
                      onChange={(e) => updateRate(idx, e.target.value)}
                      placeholder="0.00"
                      className="w-fit border px-1 py-1 rounded text-sm"
                    />
                  </td>
                  <td className="p-1 text-center">
                    <button
                      onClick={() => saveRate(r)}
                      disabled={
                        !r.group.trim() || r.rateExVAT === "" || isSaving
                      }
                      className="px-2 py-1 text-sm bg-accent text-white rounded hover:bg-primary/90 disabled:opacity-50"
                    >
                      Save
                    </button>
                  </td>
                  <td className="p-1 text-center">
                    <button
                      onClick={() => removeRate(idx)}
                      disabled={isDeleting}
                      className="p-1 text-red-500 hover:bg-red-100 rounded"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
